import { ArrowUpLong, Send } from "@/assets/icons";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  ErrorScreen,
  Loader,
  PageWrapper,
  Stepper,
} from "@/components";
import {
  DEFAULT_REQUEST_START_STEP,
  DEFAULT_REQUEST_START_SUB_STEP,
} from "@/constants";
import { appConfig } from "@/guards/auth/azure-b2c.config";
import { StepperConfig } from "@/pages/requests/config";
import { useHandlers } from "@/pages/requests/hooks/use-handlers";
import { useRequestData } from "@/pages/requests/hooks/use-request-data";
import { validationSchema } from "@/pages/requests/schema";
import {
  selectPersonnelsStore,
  selectRequestFormStore,
  useBoundStore,
} from "@/services/stores";
import { selectApplicationStore } from "@/services/stores/selectors/applications";
import { getBooleanConfigValue } from "@/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { map } from "lodash";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import styles from "./styles.scss";
import { ContractField } from "@/pages/requests/components/contract-details-form/types";
import clsx from "clsx";

export const RequestsPage: FC = () => {
  const [step, setStep] = useState(DEFAULT_REQUEST_START_STEP);
  const [subStep, setSubStep] = useState(DEFAULT_REQUEST_START_SUB_STEP);
  const [isValidStep, setIsValidStep] = useState(false);
  const [isLockedWithLoader, setIsLockedWithLoader] = useState(false);

  const { resetApplications } = useBoundStore(selectApplicationStore);

  const {
    resetRequestFormData,
    requestFormData,
    resetRequestFormDataDocs,
    removeRequestFormData,
    resetRequestFormDataPersonnels,
    resetRequestFormDataNumbers,
    isAcknowledged,
  } = useBoundStore(selectRequestFormStore);

  const { resetPersonnels } = useBoundStore(selectPersonnelsStore);

  const { ReferenceNumber: referenceNumber, domainId } = useParams();

  const resolver = zodResolver(validationSchema(subStep)[step]);

  const methods = useForm({
    mode: "onChange",
    resolver,
  });

  const {
    getValues,
    formState: { isValid, errors },
    reset,
  } = methods;

  if (getBooleanConfigValue(appConfig?.FEATURES?.IS_DEBUG_ENABLED)) {
    /* eslint-disable */
    console.log("RequestsPage", errors, isValid, getValues());
    /* eslint-enable */
  }

  const {
    isLoading: isLoadingRequestDetails,
    data: requestDetails,
    error: errorRequestDetails,
  } = useRequestData({
    referenceNumber,
    domainId,
    resetRequestFormData,
    onSetStep: setStep,
    onResetForm: reset,
  });

  useEffect(() => {
    return () => {
      resetRequestFormData();
      resetApplications();
      resetPersonnels();
    };
  }, [resetRequestFormData, resetApplications, resetPersonnels]);

  useEffect(() => {
    removeRequestFormData(ContractField.ContractType);
  }, [removeRequestFormData, requestFormData?.[ContractField.Companies]]);

  useEffect(() => {
    return () => {
      resetApplications();
      resetRequestFormDataDocs();
    };
  }, [
    resetApplications,
    resetRequestFormDataDocs,
    requestFormData?.NumberOfPersonnel,
    requestFormData?.NumberOfVehicles,
    requestFormData?.NumberOfVessels,
    requestFormData?.NumberOfCameras,
  ]);

  useEffect(() => {
    return () => {
      resetPersonnels();
      resetRequestFormDataPersonnels();
      resetRequestFormDataNumbers();
    };
  }, [
    requestFormData?.ContractType,
    resetPersonnels,
    resetRequestFormDataPersonnels,
    resetRequestFormDataNumbers,
  ]);

  useEffect(() => {
    return () => {
      resetApplications();
      resetRequestFormDataDocs();
    };
  }, [
    requestFormData?.Personnels,
    resetApplications,
    resetRequestFormDataDocs,
  ]);

  useEffect(() => {
    handleSetIsValidStep(isValid);
  }, [isValid]);

  const handleSetIsValidStep = (isValid: boolean) => {
    setIsValidStep(isValid);
  };

  const handleLockStepWithLoader = (isLocked: boolean) => {
    setIsLockedWithLoader(isLocked);
  };

  const { isSubmitting, onSubmit, onNextStep, onPrevStep } = useHandlers({
    onSetStep: setStep,
    onSetSubStep: setSubStep,
    formMethods: methods,
    onSetIsValidStep: handleSetIsValidStep,
    onSetIsLockStepWithLoader: handleLockStepWithLoader,
    step,
    subStep,
    requestDetails,
  });

  const FormComponent = StepperConfig[step].component;

  if (referenceNumber && errorRequestDetails) {
    return <ErrorScreen />;
  }

  return (
    <PageWrapper
      title="Letter of Assistance Request"
      pageWrapperTitleClassName={styles.mainTitle}
      pageWrapperContentClassName={styles.content}
      extraContentClassName={styles.stepperWrapper}
      extraContent={
        <Stepper
          steps={map(StepperConfig, ({ label }) => label)}
          activeStep={step}
        />
      }
    >
      {isLoadingRequestDetails && referenceNumber ? (
        <Loader />
      ) : (
        <FormProvider {...methods}>
          <FormComponent
            data={referenceNumber && requestDetails ? requestDetails : null}
            subStep={subStep}
            onSetIsValidStep={handleSetIsValidStep}
            onSetIsLockStepWithLoader={handleLockStepWithLoader}
          />

          <footer className={styles.actionButtons}>
            {step >= 1 ? (
              <Button
                variant={ButtonVariant.Secondary}
                size={ButtonSize.Small}
                className={styles.actionButton}
                onClick={() => onSubmit(true)}
                disabled={isSubmitting}
              >
                Save as draft
              </Button>
            ) : (
              <div />
            )}

            <div className={styles.actionButtonGroup}>
              {step > 0 && (
                <Button
                  variant={ButtonVariant.Secondary}
                  size={ButtonSize.Small}
                  className={styles.actionButton}
                  onClick={onPrevStep}
                  icon={
                    <ArrowUpLong
                      width={10}
                      height={10}
                      className={styles.actionButtonPrev}
                    />
                  }
                  disabled={isSubmitting}
                >
                  <span>Return</span>
                </Button>
              )}

              {step < StepperConfig.length - 1 ? (
                <Button
                  variant={ButtonVariant.Primary}
                  size={ButtonSize.Small}
                  className={styles.actionButton}
                  onClick={onNextStep}
                  disabled={!isValidStep || isSubmitting || isLockedWithLoader}
                  labelClassName={styles.nextButtonLabel}
                  icon={
                    <ArrowUpLong
                      width={10}
                      height={10}
                      className={styles.actionButtonNext}
                    />
                  }
                >
                  <span
                    className={clsx(isLockedWithLoader && styles.nextIsLoading)}
                  >
                    Next step
                  </span>

                  {isLockedWithLoader && <Loader diameter={20} />}
                </Button>
              ) : (
                <Button
                  variant={ButtonVariant.Primary}
                  size={ButtonSize.Small}
                  className={styles.actionButton}
                  onClick={() => onSubmit(false)}
                  icon={<Send className={styles.actionButtonSubmit} />}
                  disabled={!isAcknowledged || isSubmitting}
                >
                  <span>Submit</span>
                </Button>
              )}
            </div>
          </footer>
        </FormProvider>
      )}
    </PageWrapper>
  );
};
